import type React from 'react';

import {
  apiCreateOrUpdateMultipleFdApplicationWaterRights,
  apiDeleteFdApplicationWaterRights,
  apiDeleteFdApplicationWaterRightsByApplicationId,
} from 'constants/Api';

import type {
  IApplicationWaterRightRecord,
  IUseWaterRightsSubStepParams,
  IUseWaterRightsSubStepResult,
  IWaterRightFormState,
} from '../farmInformation.types';
import {
  EMPTY_WATER_RIGHT_FORM,
  lshCsvToArray,
  lshNormalizeEnumToken,
  lshParseWaterSourceList,
  lshSanitizeWaterSourceArray,
  lshToWaterScaleEnum,
} from '../farmInformation.utils';

export function lshUseWaterRightsSubStep({
  application,
  hasWaterRightsStepValue,
  waterRightRecords,
  waterRightForm,
  isSavingWaterRights,
  setIsSavingWaterRights,
  setWaterRightsError,
  setWaterRightForm,
  setWaterRightRecords,
  refreshJourney,
}: IUseWaterRightsSubStepParams): IUseWaterRightsSubStepResult {
  const selectedWaterSource =
    lshCsvToArray(waterRightForm.waterSource).map(lshNormalizeEnumToken)[0] ||
    '';
  const selectedHouseholdSource =
    lshCsvToArray(waterRightForm.householdConsumption).map(
      lshNormalizeEnumToken,
    )[0] || '';

  const handleWaterRightFormChange =
    (field: keyof IWaterRightFormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setWaterRightsError(null);
      setWaterRightForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };

  const handleAddWaterRightRecord = () => {
    if (!waterRightForm.scheme.trim() || !waterRightForm.permitNumber.trim()) {
      setWaterRightsError('Scheme and permit number are required.');
      return;
    }

    const waterSource = lshParseWaterSourceList(waterRightForm.waterSource);
    const householdConsumption = lshParseWaterSourceList(
      waterRightForm.householdConsumption,
    );
    const waterScale = lshToWaterScaleEnum(waterRightForm.waterScale);

    if (waterSource.invalid.length > 0) {
      setWaterRightsError(
        'Water source includes invalid values. Use Spring, Well, Municipal, Borehole, River or Dam.',
      );
      return;
    }

    if (householdConsumption.invalid.length > 0) {
      setWaterRightsError(
        'Household consumption includes invalid values. Use Spring, Well, Municipal, Borehole, River or Dam.',
      );
      return;
    }

    if (waterRightForm.waterScale.trim() && !waterScale) {
      setWaterRightsError('Water scale must be Hectares or Cubic meters.');
      return;
    }

    const waterQuantity = Number(waterRightForm.waterQuantity);

    const nextRecord: IApplicationWaterRightRecord = {
      id: waterRightForm.id,
      waterRights: true,
      waterSource: waterSource.valid,
      householdConsumption: householdConsumption.valid,
      scheme: waterRightForm.scheme.trim(),
      permitNumber: waterRightForm.permitNumber.trim(),
      schedule: waterRightForm.schedule.trim() || null,
      waterScale,
      waterQuantity: Number.isFinite(waterQuantity) ? waterQuantity : null,
      controlled: waterRightForm.controlled === 'yes',
      notes: waterRightForm.notes.trim() || null,
    };

    setWaterRightRecords((current) => [...current, nextRecord]);
    setWaterRightForm(EMPTY_WATER_RIGHT_FORM);
    setWaterRightsError(null);
  };

  const handleRemoveWaterRightRecord = async (
    record: IApplicationWaterRightRecord,
  ) => {
    if (!record.id) {
      setWaterRightRecords((current) =>
        current.filter((item) => item !== record),
      );
      return;
    }

    try {
      await apiDeleteFdApplicationWaterRights(record.id);
      setWaterRightRecords((current) =>
        current.filter((item) => item.id !== record.id),
      );
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to remove water-right record',
        error,
      );
      setWaterRightsError(
        'Could not remove this water-right record. Please try again.',
      );
    }
  };

  const handleSaveWaterRightsStep = async (): Promise<boolean> => {
    if (!application?.id || isSavingWaterRights) return false;

    try {
      setIsSavingWaterRights(true);
      setWaterRightsError(null);

      if (!hasWaterRightsStepValue) {
        await apiDeleteFdApplicationWaterRightsByApplicationId(application.id);
        setWaterRightRecords([]);
        await refreshJourney();
        return true;
      }

      if (waterRightRecords.length === 0) {
        setWaterRightsError(
          'Add at least one water-right record before continuing.',
        );
        return false;
      }

      await apiCreateOrUpdateMultipleFdApplicationWaterRights(
        waterRightRecords.map((record) => ({
          ...(record.id ? {id: Number(record.id)} : {}),
          applicationId: Number(application.id),
          waterRights: true,
          waterSource: lshSanitizeWaterSourceArray(record.waterSource),
          householdConsumption: lshSanitizeWaterSourceArray(
            record.householdConsumption,
          ),
          scheme: String(record.scheme || ''),
          permitNumber: String(record.permitNumber || ''),
          schedule: String(record.schedule || ''),
          waterScale: lshToWaterScaleEnum(String(record.waterScale || '')),
          waterQuantity:
            record.waterQuantity !== null && record.waterQuantity !== undefined
              ? Number(record.waterQuantity)
              : null,
          controlled: Boolean(record.controlled),
          notes: String(record.notes || ''),
        })),
      );

      await refreshJourney();
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save water rights',
        error,
      );
      setWaterRightsError('Could not save water rights. Please try again.');
      return false;
    } finally {
      setIsSavingWaterRights(false);
    }
  };

  return {
    selectedWaterSource,
    selectedHouseholdSource,
    handleWaterRightFormChange,
    handleAddWaterRightRecord,
    handleRemoveWaterRightRecord,
    handleSaveWaterRightsStep,
  };
}
